import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { Box, Button, Tabs, Tab, Badge } from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HistoryIcon from '@mui/icons-material/History';
import PaidIcon from '@mui/icons-material/Paid';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import Iconify from '../../../../components/Iconify';
import { useEmployees } from '../EmployessContext';
import { updateEmployeeAction } from '../../../../services/request';
import defaultImage from '../../generics/components/default.png';
import EmployeeData from './tabs/EmployeeData';
import Settlement from './tabs/Settlement';
import Attachments from './tabs/Attachments';
import EmploymentHistory from './tabs/EmploymentHistory';
import ChangeHistory from './tabs/ChangeHistory';
import History from './tabs/History';
import Reviews from './tabs/Reviews';
import SunActivePoints from './tabs/SunActivePoints';
import { ApplicationHistory } from './tabs/ApplicationHistory';
import InformationsComments from './tabs/Informations';
import AttachmentIcon from '@mui/icons-material/Attachment';
import StarIcon from '@mui/icons-material/Star';
import CommentIcon from '@mui/icons-material/Comment';
import toast from 'react-hot-toast';

const validationSchema = Yup.object({
  name: Yup.string().required('To pole nie może być puste'),
  surname: Yup.string().required('To pole nie może być puste'),
  correspondence_email: Yup.string().email('Proszę podać poprawny adres e-mail')
});

export default function EmployeeForm({ handleAddNewTab }) {
  const { rowData, rows, setRows } = useEmployees();
  const { loading, mutate } = useMutation(updateEmployeeAction);
  const [currentTab, setCurrentTab] = React.useState(0);
  const ref = React.useRef(null);
  const [preview, setPreview] = React.useState(_.isNil(rowData.photo) ? defaultImage : rowData.photo);
  const [isDefaultPhoto, setisDefaultPhoto] = React.useState(rowData.is_default_photo);

  const formik = useFormik({
    initialValues: {
      name: !_.isNil(rowData.name) ? rowData.name : '',
      surname: !_.isNil(rowData.surname) ? rowData.surname : '',
      birth_date: !_.isNil(rowData.birth_date) ? rowData.birth_date : null,
      correspondence_email: !_.isNil(rowData.correspondence_email) ? rowData.correspondence_email : '',
      phone: !_.isNil(rowData.phone) ? rowData.phone : '',
      place_of_birth: !_.isNil(rowData.place_of_birth) ? rowData.place_of_birth : '',
      pesel: !_.isNil(rowData.pesel) ? rowData.pesel : '',
      company_name: !_.isNil(rowData.company_name) ? rowData.company_name : '',
      state: !_.isNil(rowData.state) ? rowData.state : null,
      address: {
        city: !_.isNil(rowData.address?.city) ? rowData.address.city : '',
        house: !_.isNil(rowData.address?.house) ? rowData.address.house : '',
        postal_code: !_.isNil(rowData.address?.postal_code) ? rowData.address.postal_code : '',
        street: !_.isNil(rowData.address?.street) ? rowData.address.street : ''
      },
      account_number: !_.isNil(rowData.account_number) ? rowData.account_number : '',
      nip: !_.isNil(rowData.nip) ? rowData.nip : '',
      is_photo_deleted: false,
      is_photo_changed: false
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: values => {
      const formData = new FormData();
      formData.append('data', JSON.stringify(values));
      if (!_.isNil(ref.current) && !_.isNil(ref.current.files[0])) {
        formData.append('img', ref.current.files[0]);
      }
      handleSubmit(rowData.id, formData);
    }
  });

  const handlePhotoChange = deleteImage => {
    if (deleteImage) {
      formik.setFieldValue('is_photo_deleted', true);
      setPreview(defaultImage);
    } else {
      formik.setFieldValue('is_photo_deleted', false);
      setPreview(_.isNil(rowData.photo) ? defaultImage : rowData.photo);
    }

    formik.setFieldValue('is_photo_changed', false);

    if (ref.current !== null) {
      ref.current.value = null;
    }
  };

  const handleChangeImage = e => {
    const image = e.target.files[0];
    if (['image/jpg', 'image/jpeg', 'image/png'].includes(image.type)) {
      const r = new FileReader();
      r.onload = e => {
        formik.setFieldValue('is_photo_changed', true);
        setPreview(e.target.result);
      };
      r.readAsDataURL(image);
    } else {
      handlePhotoChange(false);
    }
  };

  const handleSubmit = async (orderId, values) => {
    const { error, payload } = await mutate(orderId, values);
    if (!error) {
      formik.resetForm({
        values: { ...formik.values, is_photo_deleted: false, is_photo_changed: false }
      });
      setisDefaultPhoto(payload.is_default_photo);
      const cloneRows = _.cloneDeep(rows);
      const idx = cloneRows.findIndex(item => item.id == payload.id);
      cloneRows[idx] = payload;
      setRows(cloneRows);
    } else {
      // eslint-disable-next-line no-lonely-if
      if (_.isArray(payload)) {
        toast.error(payload[0]);
      } else {
        toast.error('Nie udało się zapisać zmian');
      }
    }
  };

  React.useEffect(() => {
    if (formik.values.state != rowData.state) {
      formik.setFieldValue('state', rowData.state);
    }
  }, [rowData]);


  return (
    <form onSubmit={formik.handleSubmit}>
      <Tabs value={currentTab} variant="scrollable" onChange={(e, value) => setCurrentTab(value)}>
        <Tab icon={<AssignmentIndIcon />} label="Dane pracownika"/>
        <Tab icon={<HistoryIcon />} label="Historia zatrudnienia"/>
        <Tab icon={<InfoIcon />} label="Informacje"/>
        <Tab icon={<PaidIcon />} label="Dane płacowe"/>
        <Tab icon={<AttachmentIcon />} label="Załączniki"/>
        <Tab icon={<Iconify icon={'mdi:clipboard-text-clock'} width={20} height={20} />} label="Historia zmian"/>
        <Tab icon={<ArticleIcon />} label="Aplikacje"/>
        <Tab icon={<HistoryIcon />} label="Historia"/>
        <Tab icon={<Badge badgeContent={rowData.points_sum} max={9999} color="primary"><StarIcon color="action" /></Badge>} label="Sun Active"/>
        <Tab icon={<Badge badgeContent={rowData.average_final_grade} color="primary"><CommentIcon /></Badge>} label="Opinie"/>
      </Tabs>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Button variant="contained" fullWidth disabled={!formik.dirty || loading} type="submit">{loading ? 'Zapisywanie...' : 'Zapisz zmiany'}</Button>
      </Box>
      <Box sx={{ p: 1, height: '65vh', overflow: 'auto' }}>
        {currentTab === 0
          ? <EmployeeData
            formik={formik}
            preview={preview}
            fileInputRef={ref}
            isDefaultPhoto={isDefaultPhoto}
            handlePhotoChange={handlePhotoChange}
            handleChangeImage={handleChangeImage}
            loading={loading}
          />
          : currentTab === 1
            ? <EmploymentHistory />
            : currentTab === 2
              ? <InformationsComments />
              : currentTab === 3
                ? <Settlement formik={formik}/>
                : currentTab === 4
                  ? <Attachments />
                  : currentTab === 5
                    ? <ChangeHistory />
                    : currentTab === 6
                      ? <ApplicationHistory rowData={rowData} handleAddNewTab={handleAddNewTab} />
                      : currentTab === 7
                        ? <History />
                        : currentTab === 8
                          ? <SunActivePoints />
                          : currentTab === 9
                            ? <Reviews />
                            : null
        }
      </Box>
    </form>
  );
};
